/**
 * MapInitializationService
 *
 * Service for initializing maps and calculating map centers from GeoJSON.
 * Extracted from controllers to provide reusable map initialization logic.
 */

import type { Feature, Polygon } from "geojson";
import type { MapCenter } from "@/types/custom";
import CoordinateValidationService from "./coordinate_validation_service";

interface MapCenterOptions {
  explicitLat?: number;
  explicitLng?: number;
  geoJson?: string;
  defaultCenter?: MapCenter;
}

export class MapInitializationService {
  private static readonly DEFAULT_CENTER: MapCenter = {
    lat: 37.7749,
    lng: -122.4194  // San Francisco
  };

  /**
   * Determines the best map center based on available data
   * Priority: explicit coordinates > GeoJSON centroid > default center
   *
   * @param options - Options containing possible center sources
   * @returns The calculated map center
   */
  static calculateMapCenter(options: MapCenterOptions): MapCenter {
    // Try explicit coordinates first
    if (options.explicitLat !== undefined &&
        options.explicitLng !== undefined &&
        CoordinateValidationService.isValidNumber(options.explicitLat) &&
        CoordinateValidationService.isValidNumber(options.explicitLng)) {
      return {
        lat: options.explicitLat,
        lng: options.explicitLng
      };
    }

    // Try to compute center from GeoJSON
    if (options.geoJson) {
      const centerFromGeoJson = this.calculateCenterFromGeoJson(options.geoJson);
      if (centerFromGeoJson) {
        return centerFromGeoJson;
      }
    }

    // Fallback to provided default or global default
    return options.defaultCenter || this.DEFAULT_CENTER;
  }

  /**
   * Calculates the centroid (center point) from a GeoJSON string
   *
   * @param geoJsonString - GeoJSON string to parse
   * @returns The calculated center or null if invalid
   */
  static calculateCenterFromGeoJson(geoJsonString: string): MapCenter | null {
    try {
      const geoJson: Feature = JSON.parse(geoJsonString);

      if (!geoJson.geometry || geoJson.geometry.type !== 'Polygon') {
        return null;
      }

      const polygon = geoJson.geometry as Polygon;
      const coordinates = polygon.coordinates[0]; // Outer ring

      if (!coordinates || coordinates.length === 0) {
        return null;
      }

      // Filter valid coordinates
      const validCoords = CoordinateValidationService.filterValidCoordinates(coordinates);

      if (validCoords.length === 0) {
        return null;
      }

      // Calculate centroid
      const lat = validCoords.reduce((sum, coord) => sum + coord[1], 0) / validCoords.length;
      const lng = validCoords.reduce((sum, coord) => sum + coord[0], 0) / validCoords.length;

      // Validate computed center
      if (!CoordinateValidationService.isValidNumber(lat) ||
          !CoordinateValidationService.isValidNumber(lng)) {
        return null;
      }

      return { lat, lng };

    } catch (error) {
      console.error('Error parsing GeoJSON:', error);
      return null;
    }
  }

  /**
   * Converts GeoJSON coordinates to path format for map rendering
   *
   * @param geoJsonString - GeoJSON string to convert
   * @returns Array of {lat, lng} points or null if invalid
   */
  static geoJsonToPath(geoJsonString: string): Array<{lat: number; lng: number}> | null {
    try {
      const geoJson: Feature = JSON.parse(geoJsonString);

      if (!geoJson.geometry || geoJson.geometry.type !== 'Polygon') {
        return null;
      }

      const polygon = geoJson.geometry as Polygon;
      const coordinates = polygon.coordinates[0]; // Outer ring

      if (!coordinates || coordinates.length === 0) {
        return null;
      }

      // Convert [lng, lat] to {lat, lng} format with validation
      const path = coordinates
        .map(coord => CoordinateValidationService.toCoordinateObject(coord))
        .filter((coord): coord is {lat: number; lng: number} => coord !== null);

      return path.length > 0 ? path : null;

    } catch (error) {
      console.error('Error converting GeoJSON to path:', error);
      return null;
    }
  }

  /**
   * Validates that a GeoJSON string contains a valid Polygon
   *
   * @param geoJsonString - GeoJSON string to validate
   * @returns true if valid polygon
   */
  static isValidPolygonGeoJson(geoJsonString: string): boolean {
    try {
      const geoJson: Feature = JSON.parse(geoJsonString);

      if (!geoJson.geometry || geoJson.geometry.type !== 'Polygon') {
        return false;
      }

      const polygon = geoJson.geometry as Polygon;
      const coordinates = polygon.coordinates[0];

      if (!coordinates || coordinates.length < 3) {
        return false; // Polygon needs at least 3 points
      }

      // Check if coordinates are valid
      const validCoords = CoordinateValidationService.filterValidCoordinates(coordinates);
      return validCoords.length >= 3;

    } catch (error) {
      return false;
    }
  }

  /**
   * Gets the default center coordinate
   *
   * @returns The default map center
   */
  static getDefaultCenter(): MapCenter {
    return { ...this.DEFAULT_CENTER };
  }
}

// Default export for convenience
export default MapInitializationService;
